import React, { useMemo, useState, useEffect } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { getExperienceCandidates } from '../../../../services/teamExperienceService'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

const PAGE_SIZE = 10

type User = {
  id: number
  fullName: string
}

type Props = {
  values: number[]
  onChange: (val: number[]) => void
  experience: any
  placeholder?: string
  minChars?: number
}

export function InviteePrivateAutocompleteField({
  values,
  onChange,
  experience,
  placeholder = 'Search member',
  minChars = 0,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)

  const [debounced, setDebounced] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 200)
    return () => clearTimeout(id)
  }, [query])

  const enableSearch = debounced.length >= minChars

  const fetchData = async (search = '') => {
    if (!experience?.id) return

    try {
      setLoading(true)

      const list = await getExperienceCandidates(experience.id, {
        limit: 1000,
        search,
      })

      setUsers(list?.results || [])
    } catch (err) {
      console.error('Error fetching candidates', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (!enableSearch) return
    fetchData(debounced.toLowerCase())
  }, [debounced])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [debounced])

  const availableUsers = useMemo(
    () => users.filter((u) => !values.includes(u.id)),
    [users, values]
  )

  const visible = useMemo(
    () => availableUsers.slice(0, visibleCount),
    [availableUsers, visibleCount]
  )

  const hasMore = availableUsers.length > visibleCount

  const handleScroll = useNearBottom(() => {
    if (hasMore) setVisibleCount((c) => c + PAGE_SIZE)
  })

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text

    const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${safe})`, 'gi')
    const parts = text.split(regex)

    return (
      <>
        {parts.map((part, index) => {
          const isMatch = part.toLowerCase() === query.toLowerCase()

          return (
            <span key={index} style={{ fontWeight: isMatch ? 700 : 300 }}>
              {part}
            </span>
          )
        })}
      </>
    )
  }

  const handleAdd = (itemId: number) => {
    if (!values.includes(itemId)) {
      onChange([...values, itemId])
    }

    setQuery('')
    setOpen(false)
  }

  const handleRemove = (itemId: number) => {
    onChange(values.filter((v) => v !== itemId))
  }

  const selectedObjects = values
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean) as User[]

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>
        {selectedObjects.map((item) => (
          <div
            key={item.id}
            className='tap-add-experience-2-category-pill tap-add-experience-2-category-pill--selected'
          >
            <span>{item.fullName}</span>

            <div onClick={() => handleRemove(item.id)}>
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ))}

        <input
          className='tap-add-experience-input-category nb-language-input'
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {loading && (
            <div className='nb-language-item'>Searching...</div>
          )}

          {!loading && visible.length === 0 && (
            <div className='nb-language-item'>No results</div>
          )}

          {!loading &&
            visible.map((item) => (
              <div
                key={item.id}
                className='nb-language-item'
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleAdd(item.id)}
              >
                {highlightMatch(item.fullName, debounced)}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}